import api from './api';

function assertReferralAvailable(body) {
  if (Number(body?.block_status) === 1) {
    throw new Error(body?.block_reason || 'Your account cannot use referrals right now.');
  }
  if (Number(body?.frontend_maintenance_mode) === 1 && Number(body?.maintenance_access) === 0) {
    throw new Error(body?.frontend_maintenance_message || 'Referrals are temporarily unavailable.');
  }
  if (body?.error === true || body?.error === 1) {
    throw new Error(body?.message || 'Unable to load referral information.');
  }
}

function readReferralData(response) {
  const body = response?.data;
  assertReferralAvailable(body);
  if (body?.dataset?.data == null) {
    throw new Error(body?.message || 'Referral information is unavailable.');
  }
  return body.dataset.data;
}

export function normalizeReferralPagination(value) {
  const currentPage = Number(value?.currentPage);
  const totalPages = Number(value?.totalPages);
  return {
    currentPage: Number.isFinite(currentPage) && currentPage > 0 ? currentPage : 1,
    totalPages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1,
  };
}

export function normalizeReferralStudent(item = {}) {
  return {
    rank: Number(item?.rank) || 0,
    studentId: item?.student_id ?? null,
    name: item?.name || 'Student',
    image: item?.image_url || '',
    score: Number(item?.score) || 0,
    isCurrentStudent: Boolean(item?.is_current_student),
  };
}

export async function fetchReferralSummary({ signal } = {}) {
  const response = await api.get('/referral/my-info', { signal });
  return readReferralData(response);
}

export async function fetchReferralHistory(page = 1, { signal } = {}) {
  const response = await api.get('/referral/history', { params: { page }, signal });
  const data = readReferralData(response);
  return {
    items: Array.isArray(data?.history) ? data.history : [],
    pagination: normalizeReferralPagination(data?.pagination),
  };
}

export async function fetchReferralLeaderboard(page = 1, timeFilter = null, { signal } = {}) {
  const params = { page };
  if (timeFilter) params.time_filter = timeFilter;
  const response = await api.get('/referral/get-referral-leaderboard-data', { params, signal });
  const data = readReferralData(response);
  return {
    topThree: (Array.isArray(data?.top_three) ? data.top_three : []).map(normalizeReferralStudent),
    students: (Array.isArray(data?.all_students) ? data.all_students : []).map(normalizeReferralStudent),
    currentStudent: data?.current_student ? normalizeReferralStudent(data.current_student) : null,
    filters: Array.isArray(data?.time_filter) ? data.time_filter.filter((item) => item?.key && item?.name) : [],
    pagination: normalizeReferralPagination(data?.pagination),
  };
}

export async function transferReferralCoins(recipientCode, coins) {
  const response = await api.post('/referral/transfer', {
    recipient_referral_code: recipientCode,
    coins,
  });
  const body = response?.data;
  assertReferralAvailable(body);
  if (body?.error !== false && body?.error !== 0) {
    throw new Error(body?.message || 'Failed to transfer coins.');
  }
  return body?.message || 'Coins transferred successfully.';
}

export function prepareReferralTransfer(recipientCode, coinsText) {
  const code = String(recipientCode || '').trim();
  const amount = String(coinsText || '').trim();
  if (!code) throw new Error('Please enter referral code.');
  if (!amount) throw new Error('Please enter coins.');
  if (!/^[1-9]\d*$/.test(amount) || !Number.isSafeInteger(Number(amount))) {
    throw new Error('Please enter valid coins.');
  }
  return { code, coins: Number(amount) };
}
